import { PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js"
import PropTypes from "prop-types"
import React, { useContext, useEffect, useMemo, useState } from "react"
import PaymentMethodDropdownOption from "src/main/Billing/PaymentMethodDropdownOption"
import { SignContractWizardContext } from "src/main/Contracts/SignContract/SignContractWizardContext"

import AlertBanner from "src/components/AlertBanner"
import Button from "src/components/Button"
import Form from "src/components/Form"
import {
  displayText,
  electronicMethodsLabel,
  iconClass,
} from "src/components/SelectPaymentMethod/payment_method_helpers"
import StripeElements from "src/components/StripeElements"

import {
  cancelPaymentMethodSetup as cancelAccountPaymentMethodSetup,
  createPaymentMethod as createAccountPaymentMethod,
  initializePaymentMethodSetup as initializeAccountPaymentMethodSetup,
} from "src/api/Account/PaymentMethods"
import {
  cancelPaymentMethodSetup as cancelGuestPaymentMethodSetup,
  createPaymentMethod as createGuestPaymentMethod,
  initializePaymentMethodSetup as initializeGuestPaymentMethodSetup,
} from "src/api/ContractQuotes/PaymentMethods"

import useSetupIntent from "src/hooks/payment_method_hooks/use_setup_intent"
import useStripeForm from "src/hooks/payment_method_hooks/use_stripe_form"
import useStripeSetup from "src/hooks/payment_method_hooks/use_stripe_setup"
import { useToast } from "src/hooks/use_toast"
import { useTracker } from "src/hooks/use_tracker"

import GuestCapturedPaymentMethod from "./PaymentMethod/GuestCapturedPaymentMethod"

const SelectPaymentMethod = () => {
  const { dispatch, state, quote, marina, surchargeCopy, isGuest, wizardCopy } =
    useContext(SignContractWizardContext)
  const showToast = useToast()
  const tracker = useTracker()

  // Memoized so useSetupIntent's mount-guarded init effect sees a stable identity.
  const createFn = useMemo(
    () =>
      isGuest
        ? createGuestPaymentMethod(quote.encodedId)
        : createAccountPaymentMethod,
    [isGuest, quote.encodedId]
  )
  const initFn = useMemo(
    () =>
      isGuest
        ? initializeGuestPaymentMethodSetup(quote.encodedId)
        : initializeAccountPaymentMethodSetup,
    [isGuest, quote.encodedId]
  )
  const cancelFn = useMemo(
    () =>
      isGuest
        ? cancelGuestPaymentMethodSetup(quote.encodedId)
        : cancelAccountPaymentMethodSetup,
    [isGuest, quote.encodedId]
  )

  const { paymentMethod: newPaymentMethod, error } = useStripeSetup(createFn)

  // The PaymentElement only reports its type through onChange, which it may
  // never fire before the boater reads the page.
  const [addingType, setAddingType] = useState(null)

  const { paymentMethod, paymentMethods } = state
  const { acceptedPaymentMethods, allowDirectPayment } = quote
  // A guest has no wallet, so the add-card flow is the default view.
  const addPaymentSelected =
    paymentMethod === "add" || (isGuest && paymentMethod === "")
  const manualPaymentSelected = paymentMethod === "manual"
  const capturedPaymentMethod = isGuest
    ? paymentMethods.find((pm) => pm.stripePaymentMethodId === paymentMethod)
    : undefined
  const selectedPaymentMethod = paymentMethods.find(
    (pm) => pm.stripePaymentMethodId === paymentMethod
  )
  const cardWhileAdding =
    addPaymentSelected &&
    acceptedPaymentMethods.includes("card") &&
    addingType !== "us_bank_account"
  const cardSelected =
    selectedPaymentMethod?.type === "PaymentMethod::Card" || cardWhileAdding
  const { activeSetupIntent } = useSetupIntent(
    addPaymentSelected,
    acceptedPaymentMethods,
    false,
    initFn,
    cancelFn
  )

  useEffect(() => {
    if (newPaymentMethod) {
      tracker.trackEvent("contracts_v2:payment_method_added", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
        payment_method_type: newPaymentMethod.type,
      })

      dispatch({
        type: "PAYMENT_METHOD_ADDED",
        payload: newPaymentMethod,
      })
    }
  }, [dispatch, newPaymentMethod, quote, marina, tracker])

  useEffect(() => {
    if (error) {
      showToast(error.message, { type: "error" })
    }
  }, [error, showToast])

  const handleSelectChange = (stripePaymentMethodId) => {
    const paymentMethodSelected = stripePaymentMethodId
    const manualPaymentMethodSelected = paymentMethodSelected === "manual"
    const isExistingPaymentMethod =
      !manualPaymentMethodSelected &&
      paymentMethodSelected !== "add" &&
      paymentMethodSelected !== ""

    if (isExistingPaymentMethod) {
      const paymentMethodObject = paymentMethods.find(
        (paymentMethod) =>
          paymentMethod.stripePaymentMethodId === paymentMethodSelected
      )

      tracker.trackEvent("contracts_v2:payment_method_selected", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
        payment_method_type: paymentMethodObject?.type,
      })
    }

    if (manualPaymentMethodSelected) {
      tracker.trackEvent("contracts_v2:payment_method_selected", {
        contract_quote_id: quote.id,
        marina_id: marina.id,
        marina_name: marina.name,
        payment_method_type: "manual",
      })
    }

    dispatch({
      type: "PAYMENT_METHOD_SELECTED",
      payload: paymentMethodSelected,
    })
  }

  const renderSurchargeNotice = () => {
    if (surchargeCopy && cardSelected) {
      return (
        <div className="my-5">
          <AlertBanner type="info">{surchargeCopy}</AlertBanner>
        </div>
      )
    }
  }

  return (
    <div className="flex flex-col space-y-6">
      <div>
        <h4 className="mb-2 font-semibold">Payment Method</h4>
        <AcceptedPaymentMethods
          acceptedPaymentMethods={acceptedPaymentMethods}
          allowDirectPayment={allowDirectPayment}
        />
      </div>
      {isGuest ? (
        capturedPaymentMethod ? (
          <GuestCapturedPaymentMethod
            paymentMethod={capturedPaymentMethod}
            dispatch={dispatch}
            copy={wizardCopy}
          />
        ) : (
          <GuestPaymentMethodChoice
            acceptedPaymentMethods={acceptedPaymentMethods}
            allowDirectPayment={allowDirectPayment}
            manualPaymentSelected={manualPaymentSelected}
            dispatch={dispatch}
          />
        )
      ) : (
        <div>
          <Form.Select.Custom
            onChange={handleSelectChange}
            value={paymentMethod}
          >
            <Form.Select.RichOption value="" hideCheck>
              Select a payment method
            </Form.Select.RichOption>
            {paymentMethods.map((paymentMethod) => (
              <Form.Select.RichOption
                key={paymentMethod.id}
                value={paymentMethod.stripePaymentMethodId}
                hideCheck
              >
                <PaymentMethodDropdownOption
                  paymentMethod={paymentMethod}
                  includeExpirationDates
                />
              </Form.Select.RichOption>
            ))}
            <Form.Select.RichOption value="add">
              Add a new payment method
            </Form.Select.RichOption>
            {allowDirectPayment && (
              <Form.Select.RichOption value="manual">
                Cash or Check
              </Form.Select.RichOption>
            )}
          </Form.Select.Custom>
          {manualPaymentSelected && (
            <div className="mt-2 px-2 text-sm text-gray-600">
              Settle with your marina directly after contract signing.
            </div>
          )}
        </div>
      )}
      {addPaymentSelected && activeSetupIntent && (
        <StripeElements
          options={{
            clientSecret: activeSetupIntent.clientSecret,
          }}
        >
          <AddPaymentMethodForm onTypeChange={setAddingType} />
        </StripeElements>
      )}
      {renderSurchargeNotice()}
    </div>
  )
}

const GuestPaymentMethodChoice = ({
  acceptedPaymentMethods,
  allowDirectPayment,
  manualPaymentSelected,
  dispatch,
}) => {
  if (!allowDirectPayment) return null

  if (manualPaymentSelected) {
    const electronicLabel = electronicMethodsLabel(acceptedPaymentMethods)

    return (
      <div className="flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:justify-between">
        <span className="min-w-0 flex-1 text-sm text-gray-600">
          Settle with your marina directly after contract signing.
        </span>
        {electronicLabel && (
          <div className="w-full lg:w-auto">
            <Button
              variant="tertiary"
              fullWidth
              onClick={() =>
                dispatch({ type: "PAYMENT_METHOD_SELECTED", payload: "add" })
              }
            >
              Pay by {electronicLabel} instead
            </Button>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex justify-end">
      <Button
        variant="tertiary"
        onClick={() =>
          dispatch({ type: "PAYMENT_METHOD_SELECTED", payload: "manual" })
        }
      >
        Pay by cash or check instead
      </Button>
    </div>
  )
}

GuestPaymentMethodChoice.propTypes = {
  acceptedPaymentMethods: PropTypes.arrayOf(PropTypes.string).isRequired,
  allowDirectPayment: PropTypes.bool.isRequired,
  manualPaymentSelected: PropTypes.bool.isRequired,
  dispatch: PropTypes.func.isRequired,
}

const AcceptedPaymentMethods = ({
  acceptedPaymentMethods,
  allowDirectPayment,
}) => {
  return (
    <div className="accepted-payment-methods flex flex-col space-y-4">
      <span>The following payment methods are accepted:</span>
      <div className="flex flex-col">
        {acceptedPaymentMethods.map((paymentMethod) => (
          <div
            key={paymentMethod}
            className="inline-grid grid-cols-[20px_auto] items-center space-x-2"
          >
            <i
              className={`${iconClass(
                paymentMethod
              )} align-self-center justify-self-center text-lg`}
            />{" "}
            <span>{displayText(paymentMethod)}</span>
          </div>
        ))}
        {allowDirectPayment && (
          <div className="inline-grid grid-cols-[20px_auto] items-center space-x-2">
            <i className="fa-solid fa-money-bills align-self-center justify-self-center text-lg" />{" "}
            <span>Cash or Check</span>
          </div>
        )}
      </div>
    </div>
  )
}

AcceptedPaymentMethods.propTypes = {
  acceptedPaymentMethods: PropTypes.arrayOf(
    PropTypes.oneOf(["card", "us_bank_account"]).isRequired
  ).isRequired,
  allowDirectPayment: PropTypes.bool.isRequired,
}

const AddPaymentMethodForm = ({ onTypeChange }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [isBankAccount, setIsBankAccount] = useState(false)

  const { handleSubmit, handleChange, isSubmitting, error, complete } =
    useStripeForm({ stripe, elements })

  const handleElementChange = (event) => {
    const {
      value: { type },
    } = event

    setIsBankAccount(type === "us_bank_account")
    onTypeChange(type)
    handleChange(event)
  }

  return (
    <form className="mt-8 flex flex-col space-y-4">
      <PaymentElement
        options={{ layout: { type: "tabs" } }}
        onChange={handleElementChange}
      />
      {complete && error && <Form.Error>{error.message}</Form.Error>}
      {isBankAccount && (
        <div className="space-y-2">
          <div className="text-xs text-gray-600">
            If your bank account isn{"'"}t listed, you can add it manually in
            your{" "}
            <a
              href="/account/settings/payment_methods"
              className="font-semibold"
            >
              account settings
            </a>
            . This process may take 1-2 business days, after which you can
            return to complete the signing process.
          </div>
        </div>
      )}
      <Button
        type="submit"
        variant="secondary"
        disabled={!complete || error}
        onClick={handleSubmit}
        isLoading={isSubmitting}
        fullWidth={true}
      >
        Save
      </Button>
    </form>
  )
}

AddPaymentMethodForm.propTypes = {
  onTypeChange: PropTypes.func.isRequired,
}

export default SelectPaymentMethod
